import { useEffect, useRef, useState, ImgHTMLAttributes, CSSProperties } from "react";

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "loading"> & {
  src?: string | null;
  /** Proportional optimized copy used on card surfaces. */
  thumbSrc?: string | null;
  alt: string;
  /** Above-the-fold? */
  eager?: boolean;
  /** Aspect ratio reservation, e.g. "16/10" */
  ratio?: string;
  /** Container className (wraps the img) */
  wrapperClassName?: string;
  /** Sizes attribute hint */
  sizes?: string;
  /** How to fit the image inside the wrapper. Default: cover */
  fit?: "cover" | "contain";
  /** CSS object-position, saved as a percentage pair. */
  position?: string | null;
  /** Preserve a portrait image in a wide frame over a blurred copy. */
  portraitContain?: boolean;
  /** Height-to-width ratio required before portrait treatment is used. */
  portraitAspectThreshold?: number;
  /** Opt into storage transforms only when the transformed output has been verified. */
  transform?: boolean;
};

export const DEFAULT_IMAGE_POSITION = "50% 25%";

const SUPABASE_PUBLIC_RE =
  /^(https?:\/\/[^/]+\/storage\/v1)\/object\/public\/([^/]+)\/(.+)$/i;

const WIDTHS = [320, 480, 640, 800, 1024, 1280, 1600];

/**
 * If the URL points to a Supabase public storage object, rewrite it to use
 * the on-the-fly image transform endpoint. Otherwise return the URL as-is.
 */
function transformUrl(src: string, width: number, quality = 75): string {
  const m = src.match(SUPABASE_PUBLIC_RE);
  if (!m) return src;
  const [, base, bucket, path] = m;
  const cleanPath = path.split("?")[0];
  return `${base}/render/image/public/${bucket}/${cleanPath}?width=${width}&quality=${quality}`;
}

function buildSrcSet(src: string): string | undefined {
  if (!SUPABASE_PUBLIC_RE.test(src)) return undefined;
  return WIDTHS.map((w) => `${transformUrl(src, w, 75)} ${w}w`).join(", ");
}

export const SmartImage = ({
  src,
  thumbSrc,
  alt,
  eager = false,
  ratio,
  wrapperClassName,
  sizes = "(min-width: 1024px) 720px, (min-width: 640px) 50vw, 100vw",
  className,
  style,
  fit = "cover",
  position = DEFAULT_IMAGE_POSITION,
  portraitContain = false,
  portraitAspectThreshold = 1,
  transform = false,
  onLoad,
  ...rest
}: Props) => {
  const [loaded, setLoaded] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setLoaded(false);
    const image = imageRef.current;
    if (!image?.complete || image.naturalWidth === 0) return;

    setIsPortrait(image.naturalHeight / image.naturalWidth > portraitAspectThreshold);
    setLoaded(true);
  }, [src, portraitAspectThreshold]);

  if (!src) {
    return (
      <div
        className={`flex items-center justify-center bg-primary-soft text-primary ${wrapperClassName ?? ""}`}
        style={ratio ? { aspectRatio: ratio, ...style } : style}
        aria-hidden
      />
    );
  }

  const displaySrc = thumbSrc || src;

  const canTransform = transform && SUPABASE_PUBLIC_RE.test(displaySrc);
  const srcSet = canTransform ? buildSrcSet(displaySrc) : undefined;
  const placeholder = canTransform ? transformUrl(displaySrc, 24, 30) : undefined;

  const wrapperStyle: CSSProperties | undefined = ratio
    ? { aspectRatio: ratio, ...style }
    : style;

  const usePortraitTreatment = portraitContain && isPortrait;
  const fitClass = fit === "contain" || usePortraitTreatment ? "object-contain" : "object-cover";
  const resolvedPosition = position || DEFAULT_IMAGE_POSITION;

  return (
    <span
      className={`relative block overflow-hidden ${wrapperClassName ?? ""}`}
      style={wrapperStyle}
    >
      {placeholder && !loaded && (
        <span
          aria-hidden
          className={`absolute inset-0 bg-center bg-no-repeat ${fit === "contain" ? "bg-contain" : "scale-110 bg-cover blur-md"}`}
          style={{ backgroundImage: `url(${placeholder})` }}
        />
      )}
      {usePortraitTreatment && loaded && (
        <img
          aria-hidden="true"
          src={canTransform ? transformUrl(displaySrc, 1024, 60) : displaySrc}
          alt=""
          className="absolute inset-0 h-full w-full scale-[1.12] object-cover blur-[24px] brightness-[0.55]"
          style={{ objectPosition: "50% 50%" }}
        />
      )}
      <img ref={imageRef} width={1200} height={800}
        {...rest}
        src={canTransform ? transformUrl(displaySrc, 1024, 75) : displaySrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        // @ts-expect-error - valid attr, not in older TS DOM
        fetchpriority={eager ? "high" : "low"}
        onLoad={(event) => {
          setIsPortrait(
            event.currentTarget.naturalHeight / event.currentTarget.naturalWidth > portraitAspectThreshold,
          );
          setLoaded(true);
          onLoad?.(event);
        }}
        style={{ objectPosition: resolvedPosition }}
        className={`relative block h-full w-full ${fitClass} transition-opacity duration-500 ${
          loaded ? "opacity-100" : "opacity-0"
        } ${className ?? ""}`}
      />
    </span>
  );
};

export default SmartImage;
